/* eslint-disable @typescript-eslint/no-empty-function */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { PrefillIdResponse } from '../../organisms/CreateAccountConsentsForm/CreateAccountConsentsForm.types';

interface Tripdetails {
  tripId?: string;
  checkInDate?: string;
  checkOutDate?: string;
  propertyId?: string;
  propertyName?: string;
  orderId?: string;
  lineItemId?: string;
  orderRevisionNumber?: string;
  reservationRevisionNumber?: string;
  requestedBy?: string;
  uxlMigrationStatus?: string;
}
export interface CreateAccountState {
  formData: {
    firstName: string;
    lastName: string;
    pinyinEnglishSurname: string;
    pinyinEnglishName: string;
    zipcode: string;
    country: { id: string; value: string };
    email: string;
    pwrd: string;
    confirmPwrd: string;
    mobilePhone: string;
    mobilePhoneCountryId: { id: string; name: string; value: string };
    verificationCode: string;
    preferredLanguage: string;
    isLinkReservationOpted?: boolean;
    isLinkReservationOTPSent?: boolean;
  };
  transactionDetails: string;
  setTransactionDetails: (value: string) => void;
  tripDetails: Tripdetails;
  setTripDetails: (value: Tripdetails) => void;
  isDTTforRandQueryParam: boolean;
  setIsDTTforRandQueryParam: (value: boolean) => void;
  formValidation: boolean;
  setFormValidation: (value: boolean) => void;
  errorKey: string[];
  setErrorKey: (value: string[]) => void;
  consents: {
    type: string;
    optin: boolean;
    ctype: string;
    method?: string;
    isAllCheckedOrUnchecked?: boolean;
  }[];
  setConsents: (
    consents: {
      type: string;
      optin: boolean;
      ctype: string;
      method?: string;
      isAllCheckedOrUnchecked?: boolean;
    }[]
  ) => void;
  joinNowEnabled: boolean;
  setJoinNowEnabled: (value: boolean) => void;
  joinNowBtnClicked: boolean;
  setJoinNowBtnClicked: (value: boolean) => void;
  remembermeCheckBox: boolean;
  setRemembermeCheckBox: (value: boolean) => void;
  errorsList: string[];
  setErrorsList: (errors: string[]) => void;
  setFormData: (data: Partial<CreateAccountState['formData']>) => void;
  checkedItems: {
    key?: string;
    checkboxId: string;
    checked: boolean;
  }[];
  setCheckedItems: (checkedItems: { key?: string; checkboxId: string; checked: boolean }[]) => void;
  initialCheckedItems: {
    key?: string;
    checkboxId: string;
    checked: boolean;
  }[];
  setIntitalCheckedItems: (checkedItems: { key?: string; checkboxId: string; checked: boolean }[]) => void;
  consentErrors: { [key: string]: string };
  setConsentErrors: (errors: { [key: string]: string }) => void;
  validSweepEnrollObject: {
    sweepEnrollmentSourceCode?: string;
    sweepRedirectUrl?: string;
  };
  setvalidSweepEnrollObject: (data?: { sweepEnrollmentSourceCode?: string; sweepRedirectUrl?: string }) => void;
  isValidPartnerPortal: boolean;
  setIsValidPartnerPortal: (value: boolean) => void;
  goBackLabel: string;
  setGoBackLabel: (value: string) => void;
  goBackUrl: string;
  setGoBackUrl: (value: string) => void;
  isEnrollEAA: boolean;
  setIsEnrollEAA: (value: boolean) => void;
  prefillObj?: PrefillIdResponse;
  setPrefillObj?: (value: PrefillIdResponse) => void;
  resetTimer: boolean;
  setResetTimer: (value: boolean) => void;
  showSendCode: boolean;
  setShowSendCode: (value: boolean) => void;
  selectedOption: string;
  setSelectedOption: (value: string) => void;
}

const initialState: CreateAccountState = {
  formData: {
    firstName: '',
    lastName: '',
    pinyinEnglishSurname: '',
    pinyinEnglishName: '',
    zipcode: '',
    country: { id: '', value: '' },
    email: '',
    pwrd: '',
    confirmPwrd: '',
    mobilePhone: '',
    mobilePhoneCountryId: { id: '', name: '', value: '' },
    verificationCode: '',
    preferredLanguage: '',
    isLinkReservationOpted: false,
    isLinkReservationOTPSent: false,
  },
  consents: [
    { type: 'CORE_REQUIRED_CONSENT', optin: false, ctype: 'userConsents' },
    { type: 'PERSONALIZED_MARKETING_CONSENT', optin: false, ctype: 'userConsents' },
    { type: 'subscriptionsOffers', optin: false, ctype: 'communicationPreferences' },
    { type: 'subscriptions3rdParty', optin: false, ctype: 'communicationPreferences' },
  ],
  tripDetails: {},
  setTripDetails: () => {},
  isDTTforRandQueryParam: false,
  setIsDTTforRandQueryParam: () => {},
  transactionDetails: '',
  setTransactionDetails: () => {},
  setConsents: () => {},
  joinNowEnabled: false,
  setJoinNowEnabled: () => {},
  formValidation: false, // Initially set to false, this value will be changed to true only after all front-end validations are completed.
  setFormValidation: () => {},
  errorKey: [],
  setErrorKey: () => {},
  joinNowBtnClicked: false, // Initially set to false, this value will be changed to true only after the user clicks the "Join Now" button and the form validation is successful. After the API call, the value will be changed back to false.
  setJoinNowBtnClicked: () => {},
  remembermeCheckBox: true,
  setRemembermeCheckBox: () => {},
  errorsList: [],
  setErrorsList: () => {},
  setFormData: () => {},
  checkedItems: [],
  setCheckedItems: () => {},
  initialCheckedItems: [],
  setIntitalCheckedItems: () => {},
  consentErrors: {},
  setConsentErrors: () => {},
  validSweepEnrollObject: {
    sweepEnrollmentSourceCode: '',
    sweepRedirectUrl: '',
  },
  setvalidSweepEnrollObject: () => {},
  isValidPartnerPortal: false,
  setIsValidPartnerPortal: () => {},
  goBackLabel: '',
  setGoBackLabel: () => {},
  goBackUrl: '',
  setGoBackUrl: () => {},
  isEnrollEAA: false,
  setIsEnrollEAA: () => {},
  prefillObj: {},
  setPrefillObj: () => {},
  resetTimer: false,
  setResetTimer: () => {},
  showSendCode: false,
  setShowSendCode: () => {},
  selectedOption: 'email',
  setSelectedOption: () => {},
};

const createAccountStore: StateCreator<CreateAccountState> = set => ({
  ...initialState,
  setConsents: (consents: CreateAccountState['consents']) => {
    set(() => ({
      consents,
    }));
  },
  setTransactionDetails: (value: string) => {
    set(() => ({
      transactionDetails: value,
    }));
  },
  setTripDetails: (value: Tripdetails) => {
    set(() => ({
      tripDetails: value,
    }));
  },
  setIsDTTforRandQueryParam: (value: boolean) => {
    set(() => ({
      isDTTforRandQueryParam: value,
    }));
  },
  setJoinNowEnabled: (value: boolean) => {
    set(() => ({
      joinNowEnabled: value,
    }));
  },
  setFormValidation: (value: boolean) => {
    set(() => ({
      formValidation: value,
    }));
  },
  setErrorKey: (value: string[]) => {
    set(() => ({
      errorKey: value,
    }));
  },
  setFormData: (data: Partial<CreateAccountState['formData']>) => {
    set((state: CreateAccountState) => ({
      formData: {
        ...state.formData,
        ...data,
      },
    }));
  },
  setJoinNowBtnClicked: (value: boolean) => {
    set({ joinNowBtnClicked: value });
  },
  setRemembermeCheckBox: (value: boolean) => {
    set({ remembermeCheckBox: value });
  },
  setErrorsList: (errors: string[]) => {
    set(() => ({
      errorsList: errors,
    }));
  },
  setCheckedItems: (checkedItems: { key?: string; checkboxId: string; checked: boolean }[]) => {
    set(() => ({
      checkedItems,
    }));
  },
  setIntitalCheckedItems: (initialCheckedItems: { key?: string; checkboxId: string; checked: boolean }[]) => {
    set(() => ({
      initialCheckedItems,
    }));
  },
  setConsentErrors: (errors: { [key: string]: string }) => {
    set(() => ({
      consentErrors: errors,
    }));
  },
  setvalidSweepEnrollObject: (value?: { sweepEnrollmentSourceCode?: string; sweepRedirectUrl?: string }) => {
    set({ validSweepEnrollObject: value });
  },
  setIsValidPartnerPortal: (value: boolean) => {
    set(() => ({
      isValidPartnerPortal: value,
    }));
  },
  setGoBackLabel: (value: string) => {
    set(() => ({
      goBackLabel: value,
    }));
  },
  setGoBackUrl: (value: string) => {
    set(() => ({
      goBackUrl: value,
    }));
  },
  setIsEnrollEAA: (value: boolean) => {
    set(() => ({
      isEnrollEAA: value,
    }));
  },
  setPrefillObj: (value: PrefillIdResponse) => {
    set(() => ({
      prefillObj: value,
    }));
  },
  setResetTimer: (value: boolean) => {
    set(() => ({
      resetTimer: value,
    }));
  },
  setShowSendCode: (value: boolean) => {
    set(() => ({
      showSendCode: value,
    }));
  },
  setSelectedOption: (value: string) => {
    set(() => ({
      selectedOption: value,
    }));
  },
});

export const useCreateAccountStore = createAppStore(createAccountStore, {
  usePersistentStore: false,
});
